/**
 * AnswerChoicesGrid - Shared 2x2 answer grid component
 *
 * Used across all PreK assessment question screens to display
 * answer choices in a consistent layout.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../preKConstants';

interface AnswerChoicesGridProps {
  choices: number[];
  correctAnswer: number;
  wrongAnswer: number | null;
  hasAnswered: boolean;
  waitingForCorrectAnswer: boolean;
  disabled?: boolean;
  onChoiceClick: (choice: number) => void;
  /** Highlight the correct answer with pulsing animation (for demo/We-Do phases) */
  highlightCorrect?: boolean;
  /** The answer that was selected (for neutral feedback mode) */
  selectedAnswer?: number | null;
  /** When true, show neutral gray feedback instead of red/green (for quiz mode) */
  neutralFeedbackMode?: boolean;
  /** When true, use quick fade-in instead of staggered slide-in animation */
  minimalAnimation?: boolean;
}

const AnswerChoicesGrid: React.FC<AnswerChoicesGridProps> = ({
  choices,
  correctAnswer,
  wrongAnswer,
  hasAnswered,
  waitingForCorrectAnswer,
  disabled = false,
  onChoiceClick,
  highlightCorrect = false,
  selectedAnswer = null,
  neutralFeedbackMode = false,
  minimalAnimation = false,
}) => {
  const getButtonClasses = (choice: number): string => {
    const baseClasses =
      'h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300';

    // Neutral feedback mode (quiz phase) - no red/green, soft blue highlight
    if (neutralFeedbackMode && hasAnswered) {
      if (selectedAnswer === choice) {
        // Soft blue highlight for selected answer - positive acknowledgment
        return `${baseClasses} bg-blue-400 text-white border-4 border-blue-500 shadow-blue-300`;
      }
      // Other choices in muted blue - maintains positive feel while showing hierarchy
      return `${baseClasses} bg-blue-200 text-blue-400 border-4 border-blue-300`;
    }

    // Wrong answer flash (red pulse) - only in warmup mode
    if (wrongAnswer === choice) {
      return `${baseClasses} bg-red-500 text-white border-4 border-red-700 shadow-red-300 animate-pulse`;
    }

    // Highlight correct answer (for demo/We-Do phases)
    if (highlightCorrect && choice === correctAnswer) {
      return `${baseClasses} bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse`;
    }

    if (highlightCorrect && choice !== correctAnswer) {
      return `${baseClasses} bg-gray-300 text-gray-500 border-4 border-gray-400`;
    }

    // Correct answer (green) - shown after answering
    if (hasAnswered && choice === correctAnswer) {
      return `${baseClasses} bg-green-500 text-white border-4 border-green-700 shadow-green-300`;
    }

    // Incorrect choice grayed out after answering
    if (hasAnswered && choice !== correctAnswer) {
      return `${baseClasses} bg-gray-300 text-gray-500 border-4 border-gray-400`;
    }

    // Waiting for correct answer - highlight correct, gray others
    if (waitingForCorrectAnswer && choice === correctAnswer) {
      return `${baseClasses} bg-green-500 text-white border-4 border-green-700 shadow-green-300 animate-pulse`;
    }

    if (waitingForCorrectAnswer && choice !== correctAnswer) {
      return `${baseClasses} bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed`;
    }

    // Default interactive state
    return `${baseClasses} bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700`;
  };

  const getCursorClass = (choice: number): string => {
    if (
      disabled ||
      (hasAnswered && !waitingForCorrectAnswer) ||
      (waitingForCorrectAnswer && choice !== correctAnswer)
    ) {
      return 'cursor-not-allowed';
    }
    return 'cursor-pointer hover:shadow-2xl transform-gpu';
  };

  const renderChoiceContent = (choice: number) => {
    return <span>{choice}</span>;
  };

  return (
    <div className="grid grid-cols-2 gap-8 lg:gap-12 w-full max-w-md lg:max-w-lg">
      {choices.map((choice, index) => (
        <motion.button
          key={choice}
          initial={minimalAnimation ? { opacity: 0 } : { opacity: 0, y: 30, rotate: -5 }}
          animate={minimalAnimation ? { opacity: 1 } : { opacity: 1, y: 0, rotate: 0 }}
          transition={minimalAnimation ? { duration: 0.15 } : { duration: 0.4, delay: index * 0.1 }}
          whileHover={!disabled && !hasAnswered ? { scale: 1.08, rotate: 1 } : {}}
          whileTap={!disabled && !hasAnswered ? { scale: 0.92 } : {}}
          onClick={() => onChoiceClick(choice)}
          disabled={disabled || hasAnswered}
          className={`${getButtonClasses(choice)} ${getCursorClass(choice)}`}
          style={NUMERAL_FONT_STYLE}
        >
          {renderChoiceContent(choice)}
        </motion.button>
      ))}
    </div>
  );
};

export default AnswerChoicesGrid;
